<template>
  <div class="evidence-process-container">
    <!-- 头部区域：诊断主标题、对齐离线分析的呼吸灯标签、右侧进度计数 -->
    <div class="section-title-row d-flex align-center justify-space-between mb-4">
      <div class="d-flex align-center flex-wrap ga-3">
        <h4 class="process-main-title mb-0">
          正在进行单告警码证据链深度诊断
        </h4>
        <Transition name="phase-status" mode="out-in">
          <span :key="phase" class="phase-label" :class="`phase-${phase}`">
            <span class="phase-dot" aria-hidden="true" />
            {{ phaseText }}
          </span>
        </Transition>
      </div>

      <div class="step-progress-counter">
        已完成 {{ completedStepsCount }}/{{ steps.length }} 步骤
      </div>
    </div>

    <!-- 过程副标题描述 -->
    <div class="process-sub-desc text-caption text-slate-500 mb-4">
      正在调取告警前后的运行数据，比对指标是否达到触发条件，并生成现场排查建议
    </div>

    <!-- 核心步骤列表 -->
    <ol class="process-steps-live">
      <li
        v-for="step in steps"
        :key="step.id"
        :class="`step-${step.status}`"
      >
        <span class="step-icon">
          <span v-if="step.status === 'completed'" class="icon-check">✓</span>
          <span v-else-if="step.status === 'failed'" class="icon-failed">!</span>
          <span v-else-if="step.status === 'started'" class="icon-loading" />
          <span v-else class="icon-pending">•</span>
        </span>
        <span class="step-text">{{ step.text }}</span>
        <span v-if="getDisplayStatus(step)" class="step-status">{{ getDisplayStatus(step) }}</span>
      </li>
    </ol>

    <!-- 辅助数据源区：调取的运行数据 -->
    <div v-if="sourceItems.length" class="tool-list">
      <div class="tool-list-title d-flex align-center justify-space-between mb-3">
        <div class="d-flex align-center ga-2">
          <v-icon size="16" color="#2563eb">mdi-database-search-outline</v-icon>
          <span class="font-weight-bold text-slate-800">调取的运行数据</span>
        </div>
        <span class="tool-count-badge">
          已就绪 {{ completedSourcesCount }}/{{ sourceItems.length }} 项
        </span>
      </div>

      <div class="tool-items">
        <div
          v-for="src in sourceItems"
          :key="src.id"
          class="tool-item"
          :class="`tool-state-${src.status}`"
        >
          <div class="tool-icon-col">
            <span v-if="src.status === 'started'" class="tool-icon-loading" />
            <v-icon
              v-else
              size="16"
              :color="src.status === 'failed' ? '#dc2626' : src.status === 'completed' ? (src.rowCount === 0 ? '#64748b' : '#16a34a') : '#94a3b8'"
            >
              {{ src.status === 'completed' && src.rowCount === 0 ? 'mdi-information-outline' : sourceStatusIcon(src.status) }}
            </v-icon>
          </div>

          <div class="tool-copy">
            <div class="d-flex align-center flex-wrap ga-2">
              <span class="tool-name">{{ src.label }}</span>
              <span v-if="src.rowCount != null && src.rowCount > 0" class="text-caption text-slate-500 font-weight-medium">
                ({{ src.rowCount }} 条数据)
              </span>
            </div>
            <span class="tool-description">
              {{ src.status === 'started' ? (src.searchingDescription || '正在查询运行数据...') : src.description }}
            </span>
            <!-- 仅在查询结束且明确无数据时才展示轻量提示，查询中绝不提前透出 -->
            <div v-if="src.status === 'completed' && src.note" class="tool-note text-caption text-slate-500 mt-1">
              • {{ src.note }}
            </div>
          </div>

          <span
            class="tool-status"
            :class="[
              `tool-${src.status}`,
              src.status === 'completed' && src.rowCount === 0 ? 'tool-neutral' : ''
            ]"
          >
            {{ src.statusDisplay || sourceStatusText(src) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  steps: {
    type: Array,
    default: () => [],
  },
  sources: {
    type: Array,
    default: () => [],
  },
  phase: {
    type: String,
    default: 'connecting',
  },
})

const phaseText = computed(() => {
  switch (props.phase) {
    case 'connecting':
      return '正在准备分析'
    case 'collecting':
      return '正在查询运行数据'
    case 'verifying':
      return '正在核验告警条件'
    case 'generating':
      return '正在生成处理建议'
    case 'completed':
    case 'success':
      return '分析已完成'
    case 'failed':
      return '分析中断'
    default:
      return '正在分析'
  }
})

const completedStepsCount = computed(() => {
  return props.steps.filter(s => s.status === 'completed').length
})

const sourceItems = computed(() => props.sources || [])
const completedSourcesCount = computed(() => {
  return sourceItems.value.filter(s => s.status === 'completed').length
})

function getDisplayStatus(step) {
  if (step.statusDisplay) return step.statusDisplay
  if (step.status === 'completed') return '已完成'
  if (step.status === 'started') return '进行中...'
  if (step.status === 'failed') return '失败'
  return ''
}

function sourceStatusIcon(status) {
  if (status === 'completed') return 'mdi-check-circle-outline'
  if (status === 'failed') return 'mdi-alert-circle-outline'
  return 'mdi-clock-outline'
}

function sourceStatusText(src) {
  if (typeof src === 'string') {
    if (src === 'completed') return '已获取'
    if (src === 'failed') return '获取失败'
    if (src === 'started') return '查询中...'
    return '等待查询'
  }
  const status = src?.status
  if (status === 'completed') {
    if (src.rowCount === 0) return '暂无数据'
    return '已获取'
  }
  if (status === 'failed') return '获取失败'
  if (status === 'started') return '查询中...'
  return '等待查询'
}
</script>

<style scoped lang="scss">
.evidence-process-container {
  padding: 22px 24px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);

  /* 标题与呼吸灯 */
  .section-title-row {
    .process-main-title {
      font-size: 15px;
      font-weight: 700;
      color: #0f172a;
      line-height: 1.4;
    }

    .phase-label {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 0;
      padding: 3.5px 10px;
      border-radius: 999px;
      background: rgba(37, 99, 235, 0.08);
      color: #2563eb;
      font-size: 12px;
      font-weight: 600;

      .phase-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
      }

      &.phase-completed,
      &.phase-success {
        background: rgba(22, 163, 74, 0.1);
        color: #16a34a;
      }

      &.phase-failed {
        background: rgba(220, 38, 38, 0.08);
        color: #dc2626;
      }

      &.phase-connecting .phase-dot,
      &.phase-collecting .phase-dot,
      &.phase-verifying .phase-dot,
      &.phase-generating .phase-dot {
        animation: phase-dot-pulse 1.35s ease-in-out infinite;
      }
    }

    .step-progress-counter {
      font-size: 12px;
      font-weight: 600;
      color: #2563eb;
      background: rgba(37, 99, 235, 0.06);
      padding: 3px 10px;
      border-radius: 6px;
    }
  }

  .process-sub-desc {
    line-height: 1.4;
  }

  /* 核心步骤列表（整站分析与离线分析 1:1 标准规范） */
  .process-steps-live {
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 0;
      font-size: 13.5px;
      transition: all 0.25s ease;
      border-bottom: 1px dashed rgba(0, 0, 0, 0.04);

      &:last-child {
        border-bottom: none;
      }

      .step-icon {
        width: 22px;
        height: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        border-radius: 50%;
        font-size: 12px;
      }

      .icon-check {
        color: #16a34a;
        font-weight: 700;
      }

      /* 旋转 loading 圈圈：纯正科技蓝，匀速转动 */
      .icon-loading {
        width: 14px;
        height: 14px;
        border: 2px solid #2563eb;
        border-top-color: transparent;
        border-radius: 50%;
        animation: process-spin 0.75s linear infinite;
      }

      .icon-pending {
        color: rgba(71, 85, 105, 0.35);
        font-size: 11px;
      }

      .icon-failed {
        color: #dc2626;
        font-weight: 700;
      }

      .step-text {
        flex: 1;
        line-height: 1.45;
      }

      .step-status {
        color: #64748b;
        font-size: 12px;
        font-weight: 500;
      }

      &.step-completed {
        color: #1e293b;

        .step-icon {
          background: rgba(22, 163, 74, 0.1);
        }

        .step-status {
          color: #16a34a;
          font-weight: 600;
        }
      }

      &.step-started {
        color: #2563eb;
        font-weight: 600;

        .step-icon {
          background: rgba(37, 99, 235, 0.08);
        }

        .step-status {
          color: #2563eb;
          font-weight: 600;
        }
      }

      &.step-pending {
        color: rgba(71, 85, 105, 0.65);
      }

      &.step-failed {
        color: #dc2626;

        .step-icon {
          background: rgba(220, 38, 38, 0.08);
        }

        .step-status {
          color: #dc2626;
          font-weight: 600;
        }
      }
    }
  }

  /* 辅助数据源支撑区 */
  .tool-list {
    margin-top: 18px;
    padding-top: 16px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);

    .tool-list-title {
      color: rgba(30, 41, 59, 0.85);
      font-size: 13px;

      .tool-count-badge {
        font-size: 11.5px;
        color: #2563eb;
        background: rgba(37, 99, 235, 0.06);
        padding: 2px 8px;
        border-radius: 4px;
        font-weight: 600;
      }
    }

    .tool-items {
      display: flex;
      flex-direction: column;
      gap: 9px;
    }

    .tool-item {
      display: grid;
      grid-template-columns: 20px minmax(0, 1fr) auto;
      align-items: center;
      min-height: 40px;
      gap: 12px;
      padding: 8px 14px;
      border-radius: 8px;
      background: #f8fafc;
      border: 1px solid #f1f5f9;
      color: rgba(30, 41, 59, 0.75);
      font-size: 12px;
      transition: all 0.2s ease;

      &:hover {
        background: #f1f5f9;
        border-color: #e2e8f0;
      }

      &.tool-state-started {
        background: #eff6ff;
        border-color: #dbeafe;
      }

      &.tool-state-completed {
        background: #fcfdfc;
        border-color: #f1f5f9;
      }

      .tool-icon-col {
        display: flex;
        align-items: center;
        justify-content: center;

        .tool-icon-loading {
          width: 13px;
          height: 13px;
          border: 2px solid #2563eb;
          border-top-color: transparent;
          border-radius: 50%;
          animation: process-spin 0.75s linear infinite;
        }
      }

      .tool-copy {
        min-width: 0;

        .tool-name {
          display: block;
          color: #1e293b;
          font-weight: 600;
          font-size: 12.5px;
          line-height: 1.4;
        }

        .tool-description {
          display: block;
          min-width: 0;
          margin-top: 2px;
          color: #64748b;
          font-size: 11px;
          line-height: 1.35;
        }
      }

      .tool-status {
        flex-shrink: 0;
        font-size: 11.5px;
        font-weight: 600;

        &.tool-completed {
          color: #16a34a;
        }
        &.tool-started {
          color: #2563eb;
        }
        &.tool-pending {
          color: #94a3b8;
        }
        &.tool-neutral {
          color: #64748b;
        }
        &.tool-failed {
          color: #dc2626;
        }
      }
    }
  }
}

@keyframes process-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes phase-dot-pulse {
  0%, 100% {
    opacity: 0.45;
    transform: scale(0.82);
  }
  50% {
    opacity: 1;
    transform: scale(1.15);
    box-shadow: 0 0 10px rgba(37, 99, 235, 0.7);
  }
}
</style>
